import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const SEO = ({ title, description, keywords, image, type = 'website', schema }) => {
    const { t, language } = useLanguage();
    const { pathname } = useLocation();
    const siteUrl = 'https://amirspizza.de'; // Replace with actual domain
    const defaultImage = `${siteUrl}/images/Amir-Pizza-Logo.png`;
    const canonicalUrl = `${siteUrl}${pathname}`;

    // Phase 2: High-Volume Keywords & Specific Menu Keywords
    const baseKeywords = [
        "Amir's Pizza",
        "Amirs Pizza",
        "Pizzeria Tittling",
        "Pizza Lieferservice Tittling",
        "Pizza bestellen Tittling",
        "Essen bestellen Tittling",
        "Pizza in der Nähe",
        "Vegan Pizza Tittling",
        "Halal Pizza Tittling",
        "Familienpizza",
        "Pizza Salami",
        "Pizza Margherita"
    ].join(", ");

    // Combine page-specific keywords with base keywords
    const metaKeywords = keywords ? `${keywords}, ${baseKeywords}` : `${baseKeywords}, pizza, italian food, burgers, pasta, online bestellen`;

    // Phase 3: Optimized Title Tag
    // If a specific title is provided, use it, otherwise use the optimized default from translations
    const defaultTitle = t('defaultTitle');
    const metaTitle = title ? `${title} | Amir's Pizza Tittling` : defaultTitle;

    // Description Fallback
    const metaDescription = description || t('defaultDescription');
    const metaImage = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : defaultImage;

    // JSON-LD for LocalBusiness/Restaurant
    const defaultSchema = {
        "@context": "https://schema.org",
        "@type": ["Restaurant", "FoodDeliveryService"],
        "name": "Amir's Pizza Heimservice",
        "image": defaultImage,
        "url": siteUrl,
        "telephone": "+4985049547210",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "Bahnhofstraße 2",
            "addressLocality": "Tittling",
            "postalCode": "94104",
            "addressCountry": "DE"
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": 48.7261,
            "longitude": 13.3798
        },
        "servesCuisine": "Italian, Pizza, Fast Food, German",
        "priceRange": "€€",
        "openingHoursSpecification": [
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Wednesday", "Thursday", "Friday", "Saturday"],
                "opens": "11:00",
                "closes": "22:30"
            },
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Wednesday", "Thursday", "Friday", "Saturday"],
                "opens": "17:00",
                "closes": "22:30"
            },
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Sunday",
                "opens": "11:00",
                "closes": "22:30"
            }
        ],
        "sameAs": [
            "https://www.facebook.com/amirspizza",
            "https://www.instagram.com/amirspizza"
        ],
        "potentialAction": {
            "@type": "OrderAction",
            "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://amirspizza.de",
                "inLanguage": "de-DE",
                "actionPlatform": [
                    "http://schema.org/DesktopWebPlatform",
                    "http://schema.org/IOSPlatform",
                    "http://schema.org/AndroidPlatform"
                ]
            },
            "deliveryMethod": "http://purl.org/goodrelations/v1#DeliveryModeOwnFleet"
        }
    };

    const schemaData = schema || defaultSchema;

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{metaTitle}</title>
            <meta name="description" content={metaDescription} />
            <meta name="keywords" content={metaKeywords} />
            <link rel="canonical" href={canonicalUrl} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={metaTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:image" content={metaImage} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:site_name" content="Amir's Pizza" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={metaTitle} />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:image" content={metaImage} />

            {/* Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(schemaData)}
            </script>
        </Helmet>
    );
};

export default SEO;
